import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StringMap } from '@app/utils/string-map';
import { MonoTypeOperatorFunction, Observable, of, OperatorFunction } from 'rxjs';
import { delayWhen, filter, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  constructor(private http: HttpClient) {}

  post<T>(options: UploadOptions): Observable<Upload<T>> {
    return this.upload<T>(options, 'POST');
  }

  put<T>(options: UploadOptions): Observable<Upload<T>> {
    return this.upload(options, 'PUT');
  }

  private upload<T>(options: UploadOptions, method: string): Observable<Upload<T>> {
    const { formData = new FormData() } = options;

    if (options.file) {
      formData.append('file', options.file);
    }

    if (options.data) {
      Object.entries(options.data).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return this.http
      .request(method, options.url, {
        body: formData,
        observe: 'events',
        params: options.params || {},
        reportProgress: true,
      })
      .pipe(
        filter(
          (event) => event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response
        ),
        map((event: HttpEvent<HttpEventType.Response | HttpEventType.UploadProgress>) => {
          if (event.type === HttpEventType.UploadProgress) {
            return {
              finished: false,
              progress: Math.round((event.loaded / event.total) * 100),
            };
          } else if (event.type === HttpEventType.Response) {
            return {
              finished: true,
              progress: null,
              response: event.body,
            };
          }
        }),
        filter((value) => value != null)
      ) as Observable<Upload<T>>;
  }
}

export function delayWhenUploadIsFinished<T extends Upload<any>>(
  fn: () => Observable<any>
): MonoTypeOperatorFunction<T> {
  return delayWhen((value) => {
    if (value.finished) {
      return fn();
    } else {
      return of(value);
    }
  });
}

export function tapWhenUploadIsFinished<T extends Upload<any>>(fn: () => void): MonoTypeOperatorFunction<T> {
  return tap((upload) => {
    if (upload.finished) {
      fn();
    }
  });
}

export type UploadOptions = {
  data?: StringMap<any>;
  file?: File;
  formData?: FormData;
  params?: StringMap<any>;
  url: string;
};

export type Upload<T> = UploadInProgress | FinishedUpload<T>;

export interface FinishedUpload<T> {
  finished: true;
  progress: null;
  response: T;
}

export interface UploadInProgress {
  finished: false;
  progress: number;
}

/**
 * Maps a finished upload by calling the transform function and returning a FinishedUpload
 * object of the type or returns the UploadInProgress if the upload is still running.
 *
 * @param transform Mapping function that is called on the finished upload.
 */
export function mapFinishedUpload<T, R>(
  transform: (value: FinishedUpload<T>) => R
): OperatorFunction<Upload<T>, UploadInProgress | FinishedUpload<R>> {
  return map((upload) => {
    if (upload.finished) {
      return {
        ...upload,
        response: transform(upload),
      };
    } else {
      return upload as UploadInProgress;
    }
  });
}

export function delayFinishedUpload<T extends Upload<any>>(
  fn: () => Observable<any>
): MonoTypeOperatorFunction<T> {
  return delayWhen<T>((upload) => {
    if (upload.finished) {
      return fn();
    } else {
      return of(undefined);
    }
  });
}
